import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';
import { authFetch } from '../utils/authFetch';

export function manualAllocations(line) {
  const accounts = [line.debitAccount, line.creditAccount];
  if (accounts.some(a => /^121/.test(a?.code))) return [];
  const ids = accounts.filter(a => /^[67]/.test(a?.code)).map(a => a.id);
  return (line.cost_alloc || []).filter(a => ids.includes(a.account_id));
}

export function AllocationRows({ base, value = [], centers, onChange, disabled = false }) {
  const percentMode = value.length === 0 || value.every(a => a.percent !== null && a.percent !== undefined);
  const total = value.reduce((s, a) => s + Number(percentMode ? a.percent || 0 : a.amount || 0), 0);
  const change = (index, field, val) => onChange(value.map((a, i) => i === index ? { ...a, [field]: val } : a));
  const switchMode = mode => {
    const rows = value.length ? value : [{ cost_center_id: '', percent: 100, amount: base }];
    onChange(rows.map(a => mode === 'percent'
      ? { cost_center_id: a.cost_center_id, percent: base ? Number((Number(a.amount || 0) / Number(base) * 100).toFixed(4)) : 0 }
      : { cost_center_id: a.cost_center_id, amount: (Number(base) * Number(a.percent || 0) / 100).toFixed(2), percent: null }));
  };
  return <div>
    {!disabled && <Form.Select size="sm" className="mb-2" aria-label="Metoda de repartizare" value={percentMode ? 'percent' : 'amount'}
      onChange={e => switchMode(e.target.value)}>
      <option value="percent">Procente</option><option value="amount">Sume în lei</option>
    </Form.Select>}
    {value.map((a, i) => <div className="d-flex gap-2 mb-2" key={i}>
      <Form.Select size="sm" aria-label={`Centru ${i + 1}`} disabled={disabled} value={a.cost_center_id || ''} onChange={e => change(i, 'cost_center_id', Number(e.target.value))}>
        <option value="">Alege centrul</option>{centers.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
      </Form.Select>
      <Form.Control size="sm" style={{ maxWidth: 140 }} type="number" step={percentMode ? '0.0001' : '0.01'} aria-label={percentMode ? 'Procent' : 'Sumă repartizată'}
        disabled={disabled} value={(percentMode ? a.percent : a.amount) ?? ''} onChange={e => change(i, percentMode ? 'percent' : 'amount', e.target.value)} />
      <span>{percentMode ? '%' : 'lei'}</span>
      {!disabled && <Button type="button" size="sm" variant="outline-danger" aria-label={`Elimină centrul ${i + 1}`} onClick={() => onChange(value.filter((_, n) => n !== i))}>×</Button>}
    </div>)}
    {!disabled && <Button type="button" size="sm" variant="outline-secondary" onClick={() => onChange([...value, { cost_center_id: '', percent: percentMode ? Math.max(0, 100 - total) : null, amount: percentMode ? null : (Number(base) - total).toFixed(2) }])}>Adaugă centru</Button>}
    <div className={Math.abs(total - (percentMode ? 100 : Number(base))) < 0.000001 ? 'small text-success mt-1' : 'small text-danger mt-1'}>
      Total: {total.toLocaleString('ro-RO', { maximumFractionDigits: 4 })}{percentMode ? '% / 100%' : ` lei / ${Number(base).toLocaleString('ro-RO')} lei`}
    </div>
  </div>;
}

export function ManualCostAllocation({ lines, setLines, disabled = false }) {
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { authFetch('/repartizari-centre/config').then(setCfg).catch(e => setError(e.message)); }, []);
  if (error) return <Alert variant="danger">Nu s-a putut verifica repartizarea pe centre: {error}</Alert>;
  if (!cfg?.enabled) return null;
  const groups = lines.flatMap((line, index) => {
    if ([line.debitAccount, line.creditAccount].some(a => /^121/.test(a?.code))) return [];
    const accounts = [line.debitAccount, line.creditAccount].filter(a => /^[67]/.test(a?.code));
    return [...new Map(accounts.map(a => [a.id, a])).values()].map(account => {
      const amount = Number(line.amount || 0);
      const debit = line.debitAccount?.id === account.id ? amount : 0;
      const credit = line.creditAccount?.id === account.id ? amount : 0;
      return { line, index, account, base: account.code.startsWith('6') ? debit - credit : credit - debit };
    }).filter(g => g.base !== 0);
  });
  if (!groups.length) return null;
  return <Card className="my-3"><Card.Body><h5>Repartizare pe centre</h5>
    <p className="text-muted">Repartizați separat fiecare cheltuială și venit. La corecții, sumele urmează semnul bazei contabile.</p>
    {groups.map(({ line, index, account, base }) => <div className="mb-3" key={`${index}-${account.id}`}>
      <strong>Linia {index + 1} · {account.code} — {account.name} · {base.toLocaleString('ro-RO')} lei</strong>
      <AllocationRows base={base} centers={cfg.centers} disabled={disabled} value={(line.cost_alloc || []).filter(a => a.account_id === account.id)}
        onChange={rows => setLines(lines.map((l, i) => i === index ? { ...l, cost_alloc: [
          ...(l.cost_alloc || []).filter(a => a.account_id !== account.id && [l.debitAccount?.id, l.creditAccount?.id].includes(a.account_id)),
          ...rows.map(a => ({ ...a, account_id: account.id }))] } : l))} />
    </div>)}
  </Card.Body></Card>;
}

export default function CostAllocation({ recType, docId, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    setData(null); setError(''); setMessage('');
    authFetch(`/repartizari-centre/document/${recType}/${docId}`).then(d => { if (active) setData(d); }).catch(e => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [recType, docId, refreshKey]);
  if (error && !data) return <Alert variant="danger">Repartizare pe centre: {error}</Alert>;
  if (!data?.enabled || !data.groups?.length) return null;
  const save = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await authFetch(`/repartizari-centre/document/${recType}/${docId}`, { method: 'PUT', body: JSON.stringify({ revision: data.revision, groups: data.groups.map(g => ({
        doc_lns_id: g.doc_lns_id, account_id: g.account_id, allocations: g.allocations.map(a => ({ cost_center_id: a.cost_center_id,
          amount: a.percent != null ? null : a.amount, percent: a.percent ?? null })) })) }) });
      setData(result); setMessage('Repartizarea a fost salvată și verificată.');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return <Card className="my-3"><Card.Body><h5>Repartizare pe centre</h5>
    <p className="text-muted">Veniturile și cheltuielile de scădere a stocului au baze separate. Procentele se recalculează la modificarea valorii documentului; sumele fixe trebuie reverificate.</p>
    {error && <Alert variant="danger">{error}</Alert>}{message && <Alert variant="success">{message}</Alert>}
    {data.locked && <Alert variant="info">Repartizare disponibilă pentru consultare. La bonul de consum, centrul se schimbă în antetul bonului; la intrări, din liniile documentului. Pentru alte documente sunt necesare drept de modificare și document redeschis.</Alert>}
    <Table responsive bordered><thead><tr><th>Linie / cont</th><th>Bază contabilă</th><th>Centre</th></tr></thead><tbody>
      {data.groups.map((g, index) => <tr key={`${g.doc_lns_id}-${g.account_id}`}>
        <td>{g.doc_lns_id} · <strong>{g.code}</strong><br />{g.name}</td><td>{Number(g.base).toLocaleString('ro-RO', { minimumFractionDigits: 2 })} lei</td>
        <td style={{ minWidth: 320 }}><AllocationRows base={g.base} centers={data.centers} disabled={data.locked || busy} value={g.allocations}
          onChange={rows => { setMessage(''); setData({ ...data, groups: data.groups.map((x, i) => i === index ? { ...x, allocations: rows } : x) }); }} /></td>
      </tr>)}
    </tbody></Table>
    {!data.locked && <Button type="button" disabled={busy} onClick={save}>Salvează repartizarea</Button>}
  </Card.Body></Card>;
}
